import moment from 'moment';
import { select } from 'd3';
//
import { capitalizeFirstLetter } from 'lib/helpers/string-manipulators';
import { EDisplay } from 'components/deal-results/product-items';
import font from 'theme/visuals';

export interface IGenerateLegendTextProps {
  dealName: string;
  display?: EDisplay;
  product: string;
  periodStart: Date | null;
  periodEnd: Date | null;
  selectedCostLabel?: string | null;
  selectedForecastLabel?: string | null;
}

const toolTipConstValue = 100;

const displayType = (display: EDisplay) => {
  return display === 'sales' ? 'Revenue' : display;
};

/**
 * Create dynamic legend: "{dealName}, {product}, {display}, {dateRange}"
 * @param object Containing dealName, display, product, periodStard, and periodEnd
 * @returns String representing the label text for the given inputs
 */
export function generateLegendText({
  dealName,
  display,
  product,
  periodStart,
  periodEnd,
  selectedCostLabel,
  selectedForecastLabel,
}: IGenerateLegendTextProps): string {
  return `${dealName}, ${capitalizeFirstLetter(
    product
  )}, ${selectedForecastLabel} Forecast, ${selectedCostLabel} Cost, ${
    display ? `${capitalizeFirstLetter(displayType(display))}, ` : ''
  }${periodStart ? moment(periodStart).format('MM/YYYY') : ''} - ${
    periodEnd ? moment(periodEnd).format('MM/YYYY') : ''
  }`;
}

export function generateTooltipText(percent: number, value: number, label: string): string {
  return `${(percent * toolTipConstValue).toFixed(0)}% probability of at least ${value.toFixed(
    Math.abs(value) < toolTipConstValue ? 1 : 0
  )} ${label}`;
}

/**
 * Calculates the maximum width of an array of strings (by inserting a temporary DOM node).
 * @param ss Array of text strings
 * @param fontSize Fontsize in pixels
 * @returns Maximum text width in pixels
 */
export function measureMaxTextWidth(ss: string[], fontSize: number = font.size): number {
  const tmp = document.body.appendChild(document.createElement('span'));
  const svgTmp = select(tmp)
    .append('svg')
    .style('font-family', font.family)
    .attr('width', 0)
    .attr('height', 0)
    .append('text')
    .attr('font-size', `${fontSize}px`)
    .attr('id', `tooltip-pareto-tmp`)
    .attr('fill', 'transparent');
  const textElem: SVGTextElement | null = (document.getElementById('tooltip-pareto-tmp') as unknown) as SVGTextElement;
  let mx = 0;
  ss.forEach(s => {
    svgTmp.text(s);
    // The try-catch block was added to ensure Jest test suite works and Jest TypeScript complaints disappear.
    // The getComputedTextLength() method exists in the browser but it does not exist in JSDOM (because JSDOM
    // has not implemented the browser's layout engine)
    try {
      mx = Math.max(mx, textElem.getComputedTextLength());
      // eslint-disable-next-line no-empty
    } catch {}
  });
  select(tmp).remove();

  return mx;
}
